import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import { supabase } from './supabaseClient';

export default function SubcategoryMergeModal({ isOpen, onClose, categories = [], onComplete }) {
  const [category, setCategory] = useState('');
  const [sourceSub, setSourceSub] = useState('');
  const [mode, setMode] = useState('merge');
  const [targetSub, setTargetSub] = useState('');
  const [newName, setNewName] = useState('');
  const [productSubcats, setProductSubcats] = useState([]);
  const [sourceCount, setSourceCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [counting, setCounting] = useState(false);
  const [result, setResult] = useState(null);

  const loadProductSubcategories = useCallback(async (categoryName) => {
    if (!categoryName) { setProductSubcats([]); return; }
    try {
      let all = [];
      let from = 0;
      const batch = 1000;
      while (true) {
        const { data, error } = await supabase
          .from('products')
          .select('subcategory')
          .eq('category', categoryName)
          .not('subcategory', 'is', null)
          .range(from, from + batch - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all = all.concat(data);
        if (data.length < batch) break;
        from += batch;
      }
      const counts = {};
      all.forEach(r => {
        const s = (r.subcategory || '').trim();
        if (!s) return;
        counts[s] = (counts[s] || 0) + 1;
      });
      const list = Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setProductSubcats(list);
    } catch (err) {
      console.error('Error loading product subcategories:', err);
      setProductSubcats([]);
    }
  }, []);

  useEffect(() => {
    if (isOpen && category) {
      loadProductSubcategories(category);
      setSourceSub(''); setTargetSub(''); setNewName(''); setSourceCount(null); setResult(null);
    }
  }, [isOpen, category, loadProductSubcategories]);

  useEffect(() => {
    const run = async () => {
      if (!sourceSub || !category) { setSourceCount(null); return; }
      setCounting(true);
      try {
        const { count, error } = await supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('category', category)
          .eq('subcategory', sourceSub);
        if (error) throw error;
        setSourceCount(count ?? 0);
      } catch (err) {
        console.error('Count error:', err);
        setSourceCount(null);
      } finally { setCounting(false); }
    };
    run();
  }, [sourceSub, category]);

  const resolvedTarget = mode === 'merge' ? targetSub : newName.trim();
  const canSubmit = category && sourceSub && resolvedTarget && resolvedTarget !== sourceSub && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true); setResult(null);
    try {
      const { error: updErr } = await supabase
        .from('products')
        .update({ subcategory: resolvedTarget })
        .eq('category', category)
        .eq('subcategory', sourceSub);
      if (updErr) throw updErr;

      if (mode === 'merge') {
        await supabase.from('subcategories').delete().eq('name', sourceSub);
      } else {
        const { data: existing } = await supabase
          .from('subcategories').select('id').eq('name', sourceSub).maybeSingle();
        if (existing) {
          await supabase.from('subcategories').update({ name: resolvedTarget }).eq('id', existing.id);
        } else {
          const catObj = categories.find(c => c.name === category);
          if (catObj) await supabase.from('subcategories').insert({ name: resolvedTarget, category_id: catObj.id });
        }
      }

      setResult({ success: true, message: `Moved ${sourceCount ?? 0} product${(sourceCount ?? 0) === 1 ? '' : 's'} from "${sourceSub}" to "${resolvedTarget}".` });
      await loadProductSubcategories(category);
      setSourceSub(''); setTargetSub(''); setNewName(''); setSourceCount(null);
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Merge/rename failed:', err);
      setResult({ success: false, message: `Failed: ${err.message}` });
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;
  const mergeTargets = productSubcats.filter(s => s.name !== sourceSub);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Merge / Rename Subcategory</h2>
            <p className="text-xs text-gray-500">Reassigns products safely - no orphans</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
              <option value="">Select a category…</option>
              {categories.map(c => (<option key={c.id} value={c.name}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Subcategory to retire (source)</label>
            <select value={sourceSub} onChange={(e) => setSourceSub(e.target.value)} disabled={!category}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
              <option value="">Select subcategory…</option>
              {productSubcats.map(s => (<option key={s.name} value={s.name}>{s.name} ({s.count})</option>))}
            </select>
            {counting && <p className="text-xs text-gray-400 mt-1">Counting products…</p>}
            {sourceCount !== null && !counting && (<p className="text-xs text-gray-600 mt-1">{sourceCount} product{sourceCount === 1 ? '' : 's'} will be moved.</p>)}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode('merge')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${mode === 'merge' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-600'}`}>Merge into existing</button>
            <button type="button" onClick={() => setMode('rename')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${mode === 'rename' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-600'}`}>Rename to new</button>
          </div>
          {mode === 'merge' ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Merge into (keeper)</label>
              <select value={targetSub} onChange={(e) => setTargetSub(e.target.value)} disabled={!sourceSub}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
                <option value="">Select keeper subcategory…</option>
                {mergeTargets.map(s => (<option key={s.name} value={s.name}>{s.name} ({s.count})</option>))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">New name</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} disabled={!sourceSub}
                placeholder="e.g., JAMS, SPREADS, HONEY & SYRUP"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100" />
            </div>
          )}
          {sourceSub && resolvedTarget && resolvedTarget !== sourceSub && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-amber-800 font-medium">
                <span className="truncate max-w-[40%]">{sourceSub}</span>
                <ArrowRight size={16} className="flex-shrink-0" />
                <span className="truncate max-w-[40%]">{resolvedTarget}</span>
              </div>
              <p className="text-xs text-amber-700 mt-1">{sourceCount ?? '…'} product{sourceCount === 1 ? '' : 's'} will be reassigned. This cannot be auto-undone.</p>
            </div>
          )}
          {resolvedTarget && resolvedTarget === sourceSub && (
            <div className="flex items-center gap-2 text-sm text-red-600"><AlertTriangle size={16} /> Source and target are the same.</div>
          )}
          {result && (
            <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${result.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {result.success ? <CheckCircle size={16} className="mt-0.5 flex-shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />}
              <span>{result.message}</span>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">Close</button>
          <button onClick={handleSubmit} disabled={!canSubmit}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
            {loading ? <><Loader size={16} className="animate-spin" /> Working…</> : (mode === 'merge' ? 'Merge' : 'Rename')}
          </button>
        </div>
      </div>
    </div>
  );
}
